import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Alert, Button, CircularProgress } from '@mui/material';
import { NavBar } from './NavBar';
import { ErrorsDialog } from '@/components/Dialogs/ErrorsDialog';
import { ConfigurationDialog } from '@/components/Dialogs/ConfigurationDialog';
import { ContactDialog, InformationDialog } from '@/components/Dialogs';
import { useCompanyStore } from '@/stores/company-store';
import { useCompanies } from '@/hooks/use-companies';
import { useUIStore } from '@/stores/ui-store';
import { AccessDenied, useAuth } from '@certara/common-react-components';
import { useTranslation } from 'react-i18next';

export function MainLayout() {
  const { t } = useTranslation('load');
  const { activeCompany, setActiveCompany } = useCompanyStore();
  const [hasAccess, setHasAccess] = useState(true);
  const errorsDialogOpen = useUIStore((state) => state.errorsDialogOpen);
  const setErrorsDialogOpen = useUIStore((state) => state.setErrorsDialogOpen);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Use the unified auth hook (works for both Okta and CAD)
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated;

  // IMPORTANT: All hooks must be called at the top level, before any conditional logic
  const { data: companies, error } = useCompanies();

  useEffect(() => {
    setIsCheckingAuth(false);
  }, [auth.isPending, isAuthenticated]);

  useEffect(() => {
    if (!companies) return;
    const now = new Date();
    const validCompanies = companies.filter(
      (c) => !c.Expiration || new Date(c.Expiration) > now
    );
    setHasAccess(validCompanies.length > 0);

    if (!activeCompany && validCompanies.length === 1) {
      setActiveCompany(validCompanies[0]);
    }
  }, [companies, activeCompany, setActiveCompany]);

  if (isCheckingAuth) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" data-testid="main-layout-auth-loading">
        <CircularProgress data-testid="main-layout-auth-spinner" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        data-testid="main-layout-auth-required"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        p={3}
      >
        <Alert severity="info" sx={{ mb: 2, maxWidth: 600 }} data-testid="main-layout-auth-required-alert">
          <strong>{t('layout.authRequiredTitle')}</strong>
          <br />
          {t('layout.authRequiredBody')}
          <br />
          {t('layout.authRequiredRedirectHint')}
        </Alert>
        <Box>
          <Button
            data-testid="main-layout-auth-retry-button"
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mr: 2 }}
          >
            {t('layout.retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Companies have loaded but the user has no non-expired access — show the
  // shared full-page AccessDenied screen instead of the empty layout chrome.
  if (companies && !hasAccess) {
    return <AccessDenied />;
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }} data-testid="main-layout-container">
      <NavBar />

      {/* Dialogs - Contact Us and About must be accessible even without a space selected */}
      {errorsDialogOpen && (
        <ErrorsDialog open onClose={() => setErrorsDialogOpen(false)} />
      )}
      <ConfigurationDialog />
      <ContactDialog />
      <InformationDialog />

      <Box
        component="main"
        data-testid="main-layout-content"
        sx={{
          flexGrow: 1,
          p: 3,
          pt: 2,
          bgcolor: 'background.default',
          height: '100vh',
          overflow: 'auto',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="main-layout-companies-error-alert">
            {t('layout.failedToLoadCompanies', { message: error.message })}
          </Alert>
        )}

        {!activeCompany ? (
          <Alert
            severity="info"
            data-testid="main-layout-select-space-alert"
            action={
              companies && companies.length > 0 ? (
                <Button
                  data-testid="main-layout-select-first-button"
                  color="inherit"
                  size="small"
                  onClick={() => setActiveCompany(companies[0])}
                >
                  {t('layout.selectFirst')}
                </Button>
              ) : null
            }
          >
            {t('layout.selectSpaceToContinue')}
          </Alert>
        ) : (
          <Outlet />
        )}
      </Box>
    </Box>
  );
}
